import { useState, useEffect } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Textarea,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@rnd-payplus/alloy-react';
import { Plus, Pencil, Trash2, RefreshCw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { getCurrentTenantId } from '../utils/auth';
import { usePageShell } from '../contexts/PageShellContext';

const storageTypes = [
  { value: 'dropbox', label: 'Dropbox', icon: '📦' },
  { value: 'onedrive', label: 'OneDrive', icon: '☁️' },
  { value: 'sftp', label: 'SFTP', icon: '🖥️' },
  { value: 's3', label: 'Amazon S3', icon: '📦' },
  { value: 'azure-blob', label: 'Azure Blob Storage', icon: '💾' },
];

const emptyFormData = {
  name: '',
  storageType: '',
  tenantId: '',
  watchFolder: '/invoices/incoming',
  filePattern: '*.pdf',
  autoImport: true,
  importFrequency: 'every_5_minutes',
};

const emptyConfigData = {
  // Dropbox
  accessToken: '',
  // OneDrive
  clientId: '',
  clientSecret: '',
  // SFTP
  host: '',
  port: 22,
  username: '',
  password: '',
  authType: 'password',
  privateKey: '',
  // S3
  bucketName: '',
  region: '',
  accessKeyId: '',
  secretAccessKey: '',
};

const StorageConnectionsPage = () => {
  const [connections, setConnections] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    ...emptyFormData,
    tenantId: getCurrentTenantId(),
  });

  const [configData, setConfigData] = useState<any>({
    // Dropbox
    accessToken: '',
    // OneDrive
    clientId: '',
    clientSecret: '',
    // SFTP
    host: '',
    port: 22,
    username: '',
    password: '',
    authType: 'password',
    privateKey: '',
    // S3
    bucketName: '',
    region: '',
    accessKeyId: '',
    secretAccessKey: '',
  });

  useEffect(() => {
    loadConnections();
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await fetch('/api/tenants');
      if (!response.ok) throw new Error('Failed to load tenants');
      setTenants(await response.json());
    } catch (error) {
      console.error('Error loading tenants:', error);
      setTenants([]);
    }
  };

  const loadConnections = async () => {
    try {
      const response = await fetch('/api/storage-connections');
      const data = await response.json();
      setConnections(data);
    } catch (error) {
      console.error('Error loading storage connections:', error);
    }
  };

  const handleEditOpen = (conn: any) => {
    setEditingConnection(conn);
    // Pre-fill formData from the row's columns
    setFormData({
      name:            conn.name ?? '',
      storageType:     conn.storage_type ?? '',
      tenantId:        conn.tenant_id ?? getCurrentTenantId(),
      watchFolder:     conn.watch_folder ?? '/invoices/incoming',
      filePattern:     conn.file_pattern ?? '*.pdf',
      autoImport:      conn.auto_import ?? true,
      importFrequency: conn.import_frequency ?? 'every_5_minutes',
    });
    // Pre-fill configData. Non-credential connection fields (host, port,
    // username) ARE rehydrated from the row's columns so the user sees
    // their existing values when editing. Credentials (password,
    // privateKey, accessToken, etc.) are intentionally NOT rehydrated —
    // the user re-enters them only if they want to rotate; leaving the
    // credential fields blank causes handleSave to omit `config` from
    // the PUT body so the existing JSONB column is preserved via the
    // backend's COALESCE.
    setConfigData({
      ...emptyConfigData,
      host:     conn.host ?? '',
      port:     conn.port ?? 22,
      username: conn.username ?? '',
      authType: conn.auth_type ?? 'password',
    });
    setDialogOpen(true);
  };

  const closeDialogAndReset = () => {
    setDialogOpen(false);
    setEditingConnection(null);
    setFormData(emptyFormData);
    setConfigData(emptyConfigData);
  };

  const handleSave = async () => {
    try {
      // Build config object based on storage type. In edit mode, only
      // build it if the user actually provided new credentials —
      // otherwise omit `config` entirely so the backend's COALESCE
      // preserves the existing column.
      let config: any | undefined;

      switch (formData.storageType) {
        case 'dropbox':
          if (configData.accessToken) {
            config = { accessToken: configData.accessToken };
          }
          break;
        case 'onedrive':
          if (configData.clientId || configData.clientSecret) {
            config = {
              clientId: configData.clientId,
              clientSecret: configData.clientSecret,
            };
          }
          break;
        case 'sftp':
          if (configData.authType === 'key' && configData.privateKey) {
            config = { privateKey: configData.privateKey };
          } else if (configData.authType !== 'key' && configData.password) {
            config = { password: configData.password };
          }
          break;
        case 's3':
          if (configData.accessKeyId || configData.secretAccessKey) {
            config = {
              bucketName: configData.bucketName,
              region: configData.region,
              accessKeyId: configData.accessKeyId,
              secretAccessKey: configData.secretAccessKey,
            };
          }
          break;
      }

      // In create mode, default config to {} so the NOT NULL column gets
      // a value. In edit mode, omit it to preserve via COALESCE.
      if (!editingConnection && config === undefined) {
        config = {};
      }

      const isEdit = !!editingConnection;
      const url = isEdit
        ? `/api/storage-connections/${editingConnection.id}`
        : '/api/storage-connections';
      const method = isEdit ? 'PUT' : 'POST';
      const body: any = {
        tenantId:        formData.tenantId,
        name:            formData.name,
        watchFolder:     formData.watchFolder,
        filePattern:     formData.filePattern,
        autoImport:      formData.autoImport,
        importFrequency: formData.importFrequency,
        authType:        configData.authType,
      };
      if (!isEdit) {
        body.storageType = formData.storageType;
      }
      // SFTP-specific top-level columns. The backend stores host/port/
      // username in dedicated columns (not inside the config JSONB), so
      // they need to be passed at the top of the body. For non-SFTP
      // storage types these fields don't apply and the backend defaults
      // ('', 22, '') are fine.
      if (formData.storageType === 'sftp') {
        if (configData.host) body.host = configData.host;
        if (configData.port) body.port = configData.port;
        if (configData.username) body.username = configData.username;
      }
      if (config !== undefined) {
        body.config = config;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`Failed to ${isEdit ? 'update' : 'create'} connection (${res.status})`);
      }

      toast.success(isEdit ? 'Connection updated' : 'Connection created');
      closeDialogAndReset();
      loadConnections();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save storage connection');
    }
  };

  const handleSync = async (connectionId: string) => {
    try {
      await fetch(`http://localhost:8200/storage/${connectionId}/sync`, {
        method: 'POST',
      });
      toast.success('Sync started! Invoices will be imported in the background.');
    } catch (error) {
      toast.error('Failed to start sync');
    }
  };

  const confirmDeleteConnection = async () => {
    if (!selectedConnection) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/storage-connections/${selectedConnection.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error(`Failed to delete connection (${res.status})`);
      }
      toast.success('Connection deleted');
      setDeleteDialogOpen(false);
      setSelectedConnection(null);
      loadConnections();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete connection');
    } finally {
      setDeleting(false);
    }
  };

  const renderConfigFields = () => {
    switch (formData.storageType) {
      case 'dropbox':
        return (
          <div className="space-y-2">
            <Label htmlFor="accessToken">Dropbox Access Token</Label>
            <Input
              id="accessToken"
              type="password"
              value={configData.accessToken}
              onChange={(e) => setConfigData({ ...configData, accessToken: e.target.value })}
              placeholder="sl.xxxxxxxxxxxxx"
            />
          </div>
        );

      case 'onedrive':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clientId">Client ID</Label>
              <Input
                id="clientId"
                value={configData.clientId}
                onChange={(e) => setConfigData({ ...configData, clientId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clientSecret">Client Secret</Label>
              <Input
                id="clientSecret"
                type="password"
                value={configData.clientSecret}
                onChange={(e) => setConfigData({ ...configData, clientSecret: e.target.value })}
              />
            </div>
          </div>
        );

      case 'sftp':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="host">Host</Label>
                <Input
                  id="host"
                  value={configData.host}
                  onChange={(e) => setConfigData({ ...configData, host: e.target.value })}
                  placeholder="sftp.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="port">Port</Label>
                <Input
                  id="port"
                  type="number"
                  value={configData.port}
                  onChange={(e) => setConfigData({ ...configData, port: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={configData.username}
                  onChange={(e) => setConfigData({ ...configData, username: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="authType">Auth Type</Label>
                <Select
                  value={configData.authType}
                  onValueChange={(value) => setConfigData({ ...configData, authType: value })}
                >
                  <SelectTrigger id="authType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="password">Password</SelectItem>
                    <SelectItem value="key">Private Key</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {configData.authType === 'password' ? (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={configData.password}
                  onChange={(e) => setConfigData({ ...configData, password: e.target.value })}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="privateKey">Private Key (PEM)</Label>
                <Textarea
                  id="privateKey"
                  value={configData.privateKey}
                  onChange={(e) => setConfigData({ ...configData, privateKey: e.target.value })}
                  rows={4}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                />
                <p className="text-sm text-muted-foreground">Paste the full PEM-encoded private key</p>
              </div>
            )}
          </div>
        );

      case 's3':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bucketName">Bucket Name</Label>
              <Input
                id="bucketName"
                value={configData.bucketName}
                onChange={(e) => setConfigData({ ...configData, bucketName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={configData.region}
                onChange={(e) => setConfigData({ ...configData, region: e.target.value })}
                placeholder="us-east-1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accessKeyId">Access Key ID</Label>
              <Input
                id="accessKeyId"
                value={configData.accessKeyId}
                onChange={(e) => setConfigData({ ...configData, accessKeyId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secretAccessKey">Secret Access Key</Label>
              <Input
                id="secretAccessKey"
                type="password"
                value={configData.secretAccessKey}
                onChange={(e) => setConfigData({ ...configData, secretAccessKey: e.target.value })}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const totalConns = connections.length;
  const activeConns = connections.filter((c: any) => c.status === 'active').length;
  const totalFilesImported = connections.reduce((sum: number, c: any) => sum + (c.files_imported || 0), 0);
  const autoImportEnabled = connections.filter((c: any) => c.auto_import).length;

  usePageShell({
    title: 'Cloud Storage Connections',
    primaryAction: (
      <Button onClick={() => setDialogOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add Connection
      </Button>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Storage Connections' },
    ],
  });

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={totalConns} subtitle="Connections" />
        <KPICard title="Active" value={activeConns} subtitle="Connections" />
        <KPICard title="Files Imported" value={totalFilesImported} subtitle="Total" />
        <KPICard title="Auto-Import" value={autoImportEnabled} subtitle="Enabled" />
      </div>

      <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 mb-6 text-sm text-blue-800">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Cloud storage connections monitor folders for new invoice files and automatically OCR and route them.
          Supports Dropbox, OneDrive, SFTP, Amazon S3, and Azure Blob Storage.
        </span>
      </div>

      <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Watch Folder</TableHead>
                <TableHead>Auto Import</TableHead>
                <TableHead>Files Imported</TableHead>
                <TableHead>Last Sync</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {connections.map((conn) => (
                <TableRow key={conn.id}>
                  <TableCell>{conn.name}</TableCell>
                  <TableCell>
                    {storageTypes.find((t) => t.value === conn.storage_type)?.icon}{' '}
                    {storageTypes.find((t) => t.value === conn.storage_type)?.label}
                  </TableCell>
                  <TableCell>{conn.watch_folder}</TableCell>
                  <TableCell>
                    <Badge variant={conn.auto_import ? 'success' : 'secondary'}>
                      {conn.auto_import ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>{conn.files_imported || 0}</TableCell>
                  <TableCell>
                    {conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={conn.status === 'active' ? 'success' : 'secondary'}>
                      {conn.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSync(conn.id)}
                        title="Sync now"
                        aria-label="Sync connection"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Edit connection"
                        aria-label="Edit connection"
                        onClick={() => handleEditOpen(conn)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        title="Delete connection"
                        aria-label="Delete connection"
                        onClick={() => {
                          setSelectedConnection(conn);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {connections.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No cloud storage connections configured. Add one to enable automatic invoice import.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
      </div>

      {/* Add/Edit Connection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialogAndReset(); else setDialogOpen(true); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingConnection ? 'Edit Cloud Storage Connection' : 'Configure Cloud Storage Connection'}
            </DialogTitle>
            <DialogDescription>
              {editingConnection
                ? 'Update this connection. Leave credential fields blank to keep current values.'
                : 'Set up a new cloud storage connection for automatic invoice import.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="connectionName">Connection Name</Label>
              <Input
                id="connectionName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Dropbox Invoice Imports"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenantId">Tenant</Label>
              <Select
                value={formData.tenantId}
                onValueChange={(value) => setFormData({ ...formData, tenantId: value })}
              >
                <SelectTrigger id="tenantId">
                  <SelectValue placeholder={tenants.length === 0 ? 'Loading tenants...' : 'Select tenant'} />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storageType">Storage Type</Label>
              <Select
                value={formData.storageType}
                onValueChange={(value) => setFormData({ ...formData, storageType: value })}
                disabled={!!editingConnection}
              >
                <SelectTrigger id="storageType">
                  <SelectValue placeholder="Select storage type" />
                </SelectTrigger>
                <SelectContent>
                  {storageTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingConnection && (
                <p className="text-xs text-muted-foreground">
                  Storage type cannot be changed after creation. Delete and recreate if you need to switch.
                </p>
              )}
            </div>

            {renderConfigFields()}

            <div className="space-y-2">
              <Label htmlFor="watchFolder">Watch Folder</Label>
              <Input
                id="watchFolder"
                value={formData.watchFolder}
                onChange={(e) => setFormData({ ...formData, watchFolder: e.target.value })}
                placeholder="/invoices/incoming"
              />
              <p className="text-sm text-muted-foreground">Folder path to monitor for new invoices</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="filePattern">File Pattern</Label>
              <Input
                id="filePattern"
                value={formData.filePattern}
                onChange={(e) => setFormData({ ...formData, filePattern: e.target.value })}
                placeholder="*.pdf"
              />
              <p className="text-sm text-muted-foreground">
                File pattern to match (e.g., *.pdf, invoice_*.png)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="importFrequency">Import Frequency</Label>
              <Select
                value={formData.importFrequency}
                onValueChange={(value) => setFormData({ ...formData, importFrequency: value })}
              >
                <SelectTrigger id="importFrequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="every_minute">Every Minute</SelectItem>
                  <SelectItem value="every_5_minutes">Every 5 Minutes</SelectItem>
                  <SelectItem value="every_15_minutes">Every 15 Minutes</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="autoImport"
                checked={formData.autoImport}
                onChange={(e) => setFormData({ ...formData, autoImport: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="autoImport">Enable Auto Import</Label>
            </div>

            <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 mt-4">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                When enabled, the system will automatically monitor this storage location for new invoice files,
                OCR them, extract data, save to database, and route to configured destinations.
              </span>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSave}>
              {editingConnection ? 'Save Changes' : 'Save Connection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete storage connection</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {selectedConnection && (
            <p className="py-2 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-medium">&ldquo;{selectedConnection.name}&rdquo;</span>?
              Any SFTP poll history for this connection will be removed as well.
            </p>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={deleting}>Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={confirmDeleteConnection} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorageConnectionsPage;
